import tokens from '@kluein/design-tokens';
import { Box, Button, CheckBox, Text } from '@kluein/klue-ui';
import { NectarIcon } from '@kluein/nectar';
import { ArrowSquareOut, Sparkle } from '@phosphor-icons/react';
import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from 'contexts/auth';
import { useAskKlueLog } from 'hooks/dashboard';
import { useResponsive } from 'hooks/use-responsive';
import { AnalyticsAction } from 'lib/analytics';
import { setAskKlueResearchMode, useAskKlueResearchMode } from 'store/views';
import TEST_IDS from 'test-ids';
import TRACKING_IDS from 'tracking-ids';

import {
  StyledInputDashboard,
  StyledInputDashboardBox,
} from '../../AskKlueInput.styles';

function ClickableButtonContent({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  const { maxWidth } = useResponsive();
  const isSmallScreen = maxWidth.medium;

  return (
    <Box align="center" direction="row" fill="horizontal" gap="small">
      <StyledInputDashboardBox
        flex={{ grow: 1 }}
        align="center"
        direction="row"
        fill="horizontal"
        round="xsmall"
        justify="between"
        border={{ color: tokens.color.secondary.main, size: 'xsmall' }}
        data-test-id={TEST_IDS.askKlue.inputContainer}
        pad={{ right: tokens.spacing.xxxsmall }}
        $isExpanded={false}
        $isSmallScreen={isSmallScreen}
        $isClickableButton={true}
        onClick={onClick}
        id="ask-klue-input-container"
      >
        <Box
          background={tokens.color.secondary[100]}
          round="xsmall"
          direction="row"
          align="center"
          flex={{ grow: 1 }}
          gap="small"
          height="43px"
        >
          <Box
            direction="row"
            align="center"
            gap="xxsmall"
            pad={{ left: 'large', vertical: 'xsmall' }}
          >
            <Box
              direction="row"
              align="center"
              gap="xxsmall"
              flex={{ grow: 1 }}
            >
              <NectarIcon.KlueLogo
                color={tokens.color.secondary[800]}
                weight="regular"
                size={tokens.iconSize.large}
              />
            </Box>
          </Box>
          <Box
            flex={{ grow: 1 }}
            background={tokens.color.neutral[100]}
            round="xsmall"
            pad={{ right: 'xxsmall' }}
            style={{
              borderTopLeftRadius: '0px',
              borderBottomLeftRadius: '0px',
            }}
          >
            <Box
              direction="row"
              align="center"
              gap="xxsmall"
              justify="between"
              pad={{ horizontal: 'small' }}
              height="42px"
            >
              <Text color={tokens.color.neutral[800]}>
                {t('Common:askKlue.placeholder')}
              </Text>
              <ArrowSquareOut
                color={tokens.color.neutral[700]}
                size={tokens.iconSize.small}
              />
            </Box>
          </Box>
        </Box>
      </StyledInputDashboardBox>
    </Box>
  );
}

type AskKlueInputDashboardProps = {
  onClick?: () => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  inputValue?: string;
  onTextInputKeyPress?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  isClickableButton?: boolean;
};

function AskKlueInputDashboard({
  onClick,
  onChange,
  inputValue,
  onTextInputKeyPress,
  isClickableButton,
}: AskKlueInputDashboardProps) {
  const inputTextRef = useRef<HTMLInputElement | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const { maxWidth } = useResponsive();
  const isSmallScreen = maxWidth.medium;
  const { t } = useTranslation();
  const logAskKlueEvent = useAskKlueLog();
  const isResearchMode = useAskKlueResearchMode();

  const { isCurator } = useAuth();
  const isRMEnabled = isCurator;

  const handleToggleRM = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAskKlueResearchMode(event.target.checked);
  };

  const handleFocus = useCallback(() => {
    setIsExpanded(true);
    logAskKlueEvent({
      eventType: AnalyticsAction.focusTextInputBox,
      askKlueId: '',
    });
  }, [logAskKlueEvent]);

  const closeInput = () => {
    setTimeout(() => {
      inputTextRef?.current?.blur();
      setIsExpanded(false);
    }, 400);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    onTextInputKeyPress?.(event);
    const { key } = event;
    if (key === 'Enter') {
      closeInput();
    }
  };

  const actionContent = isRMEnabled ? (
    <Box
      direction="row"
      align="center"
      gap="xsmall"
      flex={{ grow: 0 }}
      background={tokens.color.neutral[100]}
      pad={{ left: 'xsmall', right: 'xxsmall' }}
      round={{ corner: 'right', size: 'xsmall' }}
    >
      <CheckBox
        checked={isResearchMode}
        onChange={handleToggleRM}
        label={
          <Text fontWeight="medium" color={tokens.color.neutral[1000]}>
            {t('Dashboard:askKlue.inputText.labelCheckBox')}
          </Text>
        }
        toggle={true}
        reverse
        data-tracking-id={TRACKING_IDS.askKlue.researchMode.toggleChecked}
        data-test-id={TEST_IDS.askKlue.researchMode.toggle}
      />
    </Box>
  ) : (
    <>
      {(isSmallScreen || isExpanded) && (
        <Box
          direction="row"
          align="center"
          gap="small"
          flex={{ grow: 0 }}
          background={tokens.color.neutral[100]}
          pad={{ left: 'xsmall', right: 'xxsmall' }}
          round={{ corner: 'right', size: 'xsmall' }}
          style={{
            opacity: isSmallScreen || isExpanded ? 1 : 0,
            transition: 'opacity 0.3s ease-out',
          }}
        >
          <Button
            leftSlot={<Sparkle />}
            label={t('Common:askKlue.ask')}
            size="medium"
            truncate={false}
            onClick={onClick}
            disabled={!inputValue}
            data-test-id={TEST_IDS.askKlue.button}
            data-tracking-id={TRACKING_IDS.askKlue.askKlueButton}
            style={{ height: '34px', alignItems: 'center' }}
          />
        </Box>
      )}
    </>
  );

  if (isClickableButton) {
    return (
      <ClickableButtonContent
        onClick={() => {
          // Blur input before opening modal to prevent focus conflicts
          inputTextRef?.current?.blur();
          onClick?.();
        }}
      />
    );
  }

  return (
    <Box align="center" direction="row" fill="horizontal" gap="small">
      <StyledInputDashboardBox
        flex={{ grow: 1 }}
        align="center"
        direction="row"
        fill="horizontal"
        round="xsmall"
        justify="between"
        border={{ color: tokens.color.secondary.main, size: 'xsmall' }}
        data-test-id={TEST_IDS.askKlue.inputContainer}
        pad={{ right: tokens.spacing.xxxsmall }}
        $isExpanded={isRMEnabled ? true : isExpanded}
        $isSmallScreen={isSmallScreen}
        onClick={() => {
          if (isRMEnabled) return;

          if (!isExpanded) {
            setIsExpanded(true);
            inputTextRef?.current?.focus();
          }
        }}
        id="ask-klue-input-container"
      >
        <Box
          background={tokens.color.secondary[100]}
          round="xsmall"
          direction="row"
          align="center"
          flex={{ grow: 1 }}
          gap="small"
          height="43px"
        >
          <Box
            direction="row"
            align="center"
            gap="xxsmall"
            pad={{ left: 'large', vertical: 'xsmall' }}
          >
            <Box
              direction="row"
              align="center"
              gap="xxsmall"
              flex={{ grow: 1 }}
            >
              <NectarIcon.KlueLogo
                color={tokens.color.secondary[800]}
                weight="regular"
                size={tokens.iconSize.large}
              />
            </Box>
          </Box>
          <Box
            flex={{ grow: 1 }}
            background={tokens.color.neutral[100]}
            round="xsmall"
            pad={{ right: 'xxsmall' }}
          >
            <StyledInputDashboard
              plain="full"
              size="small"
              placeholder={t('Common:askKlue.placeholder')}
              onChange={onChange}
              onKeyPress={handleKeyPress}
              value={inputValue}
              ref={inputTextRef}
              data-test-id={TEST_IDS.askKlue.textInput}
              data-tracking-id={TRACKING_IDS.askKlue.askKlueInput}
              onFocus={handleFocus}
              onBlur={closeInput}
            />
          </Box>
        </Box>
        {actionContent}
      </StyledInputDashboardBox>
    </Box>
  );
}

export default AskKlueInputDashboard;
